import * as React from "react";
import { Printer } from "lucide-react";

import anthemLogo from "@/assets/logo.webp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import "./storage-bag-tags.css";

export type BagTagItem = {
  orderItemId: string;
  productName: string;
  lotNumber?: string;
  remainingQuantity: number;
};

type Props = {
  orderId: string;
  orderNumber: string;
  customerName: string;
  purchasedAt: number;
  sandbox: boolean;
  items: BagTagItem[];
};

const TAGS_PER_SHEET = 4;
const MAX_TAGS_PER_JOB = 2_000;

export function StorageBagTags(props: Props) {
  const { orderId, orderNumber, customerName, purchasedAt, sandbox, items } = props;
  const [quantities, setQuantities] = React.useState<Record<string, string>>(() =>
    Object.fromEntries(items.map((item) => [item.orderItemId, String(item.remainingQuantity)])),
  );
  const [logoState, setLogoState] = React.useState<"loading" | "ready" | "failed">("loading");
  const [printing, setPrinting] = React.useState(false);
  const validDetails = Boolean(orderNumber.trim() && customerName.trim()) &&
    Number.isFinite(purchasedAt) && purchasedAt > 0 && Number.isFinite(new Date(purchasedAt).getTime()) &&
    items.length > 0 && items.every((item) => item.productName.trim() &&
      Number.isSafeInteger(item.remainingQuantity) && item.remainingQuantity > 0);
  const requested = items.map((item) => ({ item, quantity: Number(quantities[item.orderItemId]) }));
  const validQuantities = requested.every(({ item, quantity }) =>
    quantities[item.orderItemId]?.trim() !== "" && Number.isSafeInteger(quantity) && quantity >= 0,
  );
  const bagCount = requested.reduce((sum, row) => sum + row.quantity, 0);
  const sheetCount = requested.reduce((sum, row) => sum + Math.ceil(row.quantity / TAGS_PER_SHEET), 0);
  const validJob = validDetails && validQuantities && bagCount > 0 && sheetCount * TAGS_PER_SHEET <= MAX_TAGS_PER_JOB;
  const purchaseDate = validDetails ? new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago", month: "short", day: "numeric", year: "numeric",
  }).format(purchasedAt) : null;
  const sheets = validJob ? requested.flatMap(({ item, quantity }) =>
    Array.from({ length: Math.ceil(quantity / TAGS_PER_SHEET) }, (_, index) => ({ item, index })),
  ) : [];
  const spareTags = sheetCount * TAGS_PER_SHEET - bagCount;

  async function printTags() {
    if (!validJob || logoState !== "ready" || printing) return;
    setPrinting(true);
    try {
      await document.fonts?.ready;
      window.print();
    } finally {
      setPrinting(false);
    }
  }

  return <main className={`storage-bag-tags-page${logoState !== "ready" || !validJob ? " bag-tags-unavailable" : ""}`}>
    <div className="bag-tags-controls">
      <div className="bag-tags-heading">
        <div>
          <h1>Print bag tags</h1>
          <p>{customerName} · Purchase {orderNumber}</p>
        </div>
        <Button asChild variant="outline"><a href={`/order-resume?orderId=${encodeURIComponent(orderId)}&purpose=holding`}>Holding DO</a></Button>
      </div>
      <p>Four identical tags per Letter sheet (8.5 × 11 inches). Print at 100% scale with browser headers and footers off, then cut into quarters.</p>
      {sandbox ? <p className="bag-tags-alert">Test tags — do not attach to live inventory.</p> : null}
      {!validDetails ? <p role="alert">Purchase details are incomplete. Refresh the order before printing tags.</p> : <>
        <div className="bag-tags-quantities">
          {items.map((item) => <div className="bag-tags-quantity" key={item.orderItemId}>
            <Label htmlFor={`tag-quantity-${item.orderItemId}`}>
              <span>{item.productName}</span>
              {item.lotNumber ? <span className="bag-tags-lot">Lot {item.lotNumber}</span> : null}
            </Label>
            <Input id={`tag-quantity-${item.orderItemId}`} aria-label={`Tags needed for ${item.productName}`}
              type="number" min={0} max={MAX_TAGS_PER_JOB} step={1} value={quantities[item.orderItemId]}
              disabled={printing} onChange={(event) => setQuantities((current) => ({ ...current, [item.orderItemId]: event.target.value }))} />
          </div>)}
        </div>
        <p className="bag-tags-help">Quantities start with the bags still owned. Adjust them for replacements, or enter 0 to skip a coffee. Each coffee prints on separate sheets.</p>
        {!validJob ? <p role="alert">Enter whole bag counts, with at least one tag and no more than {MAX_TAGS_PER_JOB.toLocaleString("en-US")} printed tags per job.</p> : <p role="status" className="bag-tags-summary">{sheetCount} {sheetCount === 1 ? "sheet" : "sheets"} · {sheetCount * TAGS_PER_SHEET} tags{spareTags > 0 ? ` · ${spareTags} spare ${spareTags === 1 ? "tag" : "tags"}` : ""}</p>}
      </>}
      {logoState === "failed" ? <p role="alert">The Anthem logo could not load. Reload this page before printing.</p> : null}
      <Button type="button" onClick={() => void printTags()} disabled={!validJob || logoState !== "ready" || printing}>
        <Printer aria-hidden="true" className="size-4" />{printing ? "Preparing print…" : "Print tags"}
      </Button>
      <p className="bag-tags-help">After attaching a tag to every bag, confirm the tags and pallet locations in Customer storage.</p>
    </div>
    <div className="bag-tags-sheets" aria-label="Bag tag sheets">
      {sheets.map(({ item, index }) => <section className="bag-tag-sheet" aria-label={`${item.productName}, sheet ${index + 1}`} key={`${item.orderItemId}-${index}`}>
        {Array.from({ length: TAGS_PER_SHEET }, (_, copy) => <article className="bag-tag" key={copy} aria-label="Customer bag tag">
          <img src={anthemLogo} alt="Anthem Coffee Imports" className="bag-tag-logo"
            onLoad={() => setLogoState((state) => state === "failed" ? state : "ready")}
            onError={() => setLogoState("failed")} />
          <p className="bag-tag-status">{sandbox ? "TEST TAG · DO NOT MOVE LIVE INVENTORY" : "CUSTOMER-OWNED · HOLD"}</p>
          <div className="bag-tag-field"><span>Customer</span><strong>{customerName}</strong></div>
          <div className="bag-tag-field"><span>Purchase number</span><strong>{orderNumber}</strong></div>
          <div className="bag-tag-field"><span>Purchase date</span><strong>{purchaseDate}</strong></div>
          <div className="bag-tag-coffee"><strong>{item.productName}</strong>{item.lotNumber ? <span>Lot {item.lotNumber}</span> : null}</div>
        </article>)}
      </section>)}
    </div>
  </main>;
}
